// Pass-chart view for American / Canadian football QB throws.
//
// Each throw's start X is translated to a shared vertical "zero" line, so
// coaches can compare shape-of-throw across a session without being
// distracted by absolute field position. The pitch is drawn as a portrait
// half-pitch rectangle: forward distance runs vertically (+ = downfield),
// lateral offset runs horizontally.
//
// DESIGN DECISION: axis rotation is baked into the *coordinate* space, not
// the drawer. Callers feed `(lateralMin..lateralMax)` as the transform's
// X range and `(forwardMin..forwardMax)` as its Y range. `createTransform`
// already inverts Y for canvas, so forward ends up pointing up. This keeps
// the drawer implementation the same shape as the other sport drawers.
//
// Unit handling: this module is entirely in SI (metres). The drawer takes
// a `formatDistance(meters) -> string` callback so the caller routes
// gridline labels through the active unit system — no raw m->yd constants
// inside libs/pitch.

import type { PitchBounds, WorldToCanvas } from '../../types'
import { PITCH_FILL, PITCH_LINE, PITCH_LINE_WIDTH } from '../../constants'
import { drawArrow, drawPoint } from '../../draw-elements'
import { YARD_M, HASH_INSET_YD, HASH_LEN_YD } from './constants'

/** Arrow in world coords (metres). `x`, `endX` are forward; `y`, `endY`
 *  lateral, in the raw backend frame (`y ∈ [0, pitchWidth]`, non-centred).
 *  Callers must also pass the real `lateralCentreM` when converting, so
 *  arbitrary pitch sizes (NFL, CFL, college, practice fields) line up on
 *  the "0 = pocket centre" display axis. */
export interface PassChartArrowInput {
  id: string
  x: number
  y: number
  endX: number
  endY: number
  color: string
}

/** The same arrow after forward (X-axis) normalisation into display coords.
 *  Only the forward axis is collapsed to 0 — lateral (Y) passes through
 *  unchanged, so starts spread along the zero line at their real hash
 *  positions and the endpoint keeps its absolute lateral coordinate. */
export interface PassChartArrow {
  id: string
  /** Display x: lateral position of the throw start (preserved from world y). */
  x: number
  /** Display y: 0 — every throw's forward start collapses to the zero line. */
  y: 0
  /** Display x: lateral position of the throw end (preserved from world endY). */
  endX: number
  /** Display y: forward distance gained = world endX - world x. */
  endY: number
  color: string
}

/**
 * Normalise a raw throw into pass-chart display coordinates.
 *
 * The pass chart intentionally discards absolute field position and
 * direction-of-play so coaches can compare throw shape and target
 * distribution across an entire session.
 *
 * Transformations:
 *
 * 1. Forward-axis collapse
 *    - Every throw start is anchored to the shared zero line (`y = 0`).
 *    - The displayed forward distance is the magnitude of the throw's
 *      gain/loss (`abs(endX - x)`).
 *
 * 2. Lateral-axis mirroring
 *    - Backend coordinates use a lateral frame where increasing Y runs
 *      in the opposite direction to the desired pass-chart orientation.
 *    - Both start and end lateral positions are mirrored about the pitch
 *      centre using `pitchWidthM - lateralPosition`.
 *
 * 3. Direction normalisation
 *    - Throws are normalised to always display pointing upward from
 *      the zero line, regardless of the attacking direction they were
 *      made in.
 *    - Throws made in the alternate direction (endX < x) retain their
 *      lateral position, as they are already on the correct side of
 *      the chart.
 *    - Throws made in the normal direction are mirrored about the pitch
 *      centre (`pitchWidthM - lateralPosition`) to move them to the
 *      opposite side before display.
 *    - The forward distance for all throws is the absolute gain/loss
 *      magnitude, so every arrow extends away from the zero line in
 *      the same visual direction.
 *
 * Example:
 *
 * Raw:
 *   (x=10, y=30) -> (endX=20, endY=50)   ← normal direction, lateral mirrored
 *   (x=20, y=30) -> (endX=10, endY=50)   ← alternate direction, lateral kept
 *
 * Display:
 *   (x=pitchWidthM-30, y=0) -> (endX=pitchWidthM-50, endY=10)
 *   (x=30,             y=0) -> (endX=50,             endY=10)
 *
 * Multiple throws from different QB positions therefore appear as dots
 * distributed along the zero line at their mirrored lateral locations,
 * with arrow length representing forward distance and arrow direction
 * normalised for comparison across the session.
 */
export function toPassChartArrow(
  arrow: PassChartArrowInput,
  pitchWidth: number
): PassChartArrow {
  const isAlternateDirection = arrow.endX < arrow.x
  const normalizedX = isAlternateDirection ? arrow.y : pitchWidth - arrow.y
  const normalizedEndX = isAlternateDirection
    ? arrow.endY
    : pitchWidth - arrow.endY

  return {
    id: arrow.id,
    x: normalizedX,
    y: 0,
    endX: normalizedEndX,
    endY: Math.abs(arrow.endX - arrow.x),
    color: arrow.color
  }
}

export interface PassChartBoundsOptions {
  /** Sideline-to-sideline pitch width in metres — mandatory. Derive from
   *  the real session pitch (`pitchLateralSpan(pitch).widthM`) rather than
   *  a hardcoded sport constant. */
  pitchWidthM: number
  /** Metres of padding above the furthest forward throw. Default 2. */
  forwardPaddingM?: number
  /** Metres of padding below the furthest backward throw. Default 2. */
  backwardPaddingM?: number
}

/**
 * Build a bounding box around a set of pass-chart arrows, clamped laterally
 * to the pitch width. The purple "0" line always sits inside the box even
 * if every throw goes backward.
 *
 * Returns bounds in display coords: `minX..maxX` is the lateral extent,
 * `minY..maxY` is the forward extent.
 */
export function computePassChartBounds(
  arrows: PassChartArrow[],
  options: PassChartBoundsOptions
): PitchBounds {
  const { pitchWidthM, forwardPaddingM = 2, backwardPaddingM = 2 } = options

  // Lateral axis is in the raw-backend frame `[0, pitchWidth]` — matching
  // what `toPassChartArrow` passes through from the source arrow's `y`.
  // Using a centred `[-halfWidth, +halfWidth]` frame would offset every
  // throw by `pitchWidth/2` and pin the whole cluster to the right edge.
  let maxForward = 0
  let minForward = 0
  for (const a of arrows) {
    if (a.endY > maxForward) maxForward = a.endY
    if (a.endY < minForward) minForward = a.endY
  }

  return {
    minX: 0,
    maxX: pitchWidthM,
    minY: minForward - backwardPaddingM,
    maxY: maxForward + forwardPaddingM
  }
}

export interface PassChartDrawOptions {
  fill?: string
  line?: string
  lineWidth?: number
  /** Spacing between gridlines, in metres. Defaults to 5 yards. */
  gridSpacingM?: number
  /** Called for each gridline to produce its label. Receives the forward
   *  distance in metres. If omitted, no labels are drawn. */
  formatDistance?: (meters: number) => string
  /** Colour of the zero / start line (default: accent purple). */
  startLineColor?: string
  /** Optional highlight — arrow id to draw with a thicker stroke + halo. */
  selectedId?: string
  /** Lateral distance from each sideline to the hash-mark row, in metres.
   *  Defaults to the NFL hash inset (23.5 yd). Pass CFL's 24yd equivalent
   *  from the canadian-football constants for that sport. Set to 0 or
   *  `null` to skip hashes entirely. */
  hashInsetM?: number | null
}

const DEFAULT_START_LINE = '#a855f7'
const DEFAULT_GRID_SPACING_M = 5 * YARD_M
// Match the full AMF pitch renderer: per-yard hash ticks are 2 yards long.
const HASH_TICK_LEN_M = HASH_LEN_YD * YARD_M

/**
 * Draw the pass-chart: half-pitch rectangle, forward-distance gridlines,
 * the purple zero line, and each arrow from `(lateral, 0)` to
 * `(endLateral, forwardDistance)`.
 *
 * The passed-in `bounds` MUST be the output of `computePassChartBounds`
 * on the same arrow set — the drawer trusts them to lay out gridlines.
 */
export function drawPassChart(
  ctx: CanvasRenderingContext2D,
  arrows: PassChartArrow[],
  bounds: PitchBounds,
  w2c: WorldToCanvas,
  canvasWidth: number,
  canvasHeight: number,
  options: PassChartDrawOptions = {}
): void {
  const {
    fill = PITCH_FILL,
    line = PITCH_LINE,
    lineWidth = PITCH_LINE_WIDTH,
    gridSpacingM = DEFAULT_GRID_SPACING_M,
    formatDistance,
    startLineColor = DEFAULT_START_LINE,
    selectedId,
    hashInsetM = HASH_INSET_YD * YARD_M
  } = options

  // Background.
  ctx.fillStyle = fill
  ctx.fillRect(0, 0, canvasWidth, canvasHeight)

  const topLeft = w2c(bounds.minX, bounds.maxY)
  const topRight = w2c(bounds.maxX, bounds.maxY)
  const bottomLeft = w2c(bounds.minX, bounds.minY)
  const bottomRight = w2c(bounds.maxX, bounds.minY)

  // Outer boundary.
  ctx.strokeStyle = line
  ctx.lineWidth = lineWidth
  ctx.lineJoin = 'miter'
  ctx.beginPath()
  ctx.moveTo(topLeft.cx, topLeft.cy)
  ctx.lineTo(topRight.cx, topRight.cy)
  ctx.lineTo(bottomRight.cx, bottomRight.cy)
  ctx.lineTo(bottomLeft.cx, bottomLeft.cy)
  ctx.closePath()
  ctx.stroke()

  // Gridlines at +/- k * gridSpacingM for k = 1..N, skipping 0 (drawn
  // separately as the start line). Labels route through formatDistance so
  // the caller can pick the right unit system.
  const startStep = Math.ceil(bounds.minY / gridSpacingM)
  const endStep = Math.floor(bounds.maxY / gridSpacingM)
  ctx.lineWidth = Math.max(1, lineWidth * 0.6)
  ctx.strokeStyle = line
  ctx.globalAlpha = 0.5
  for (let step = startStep; step <= endStep; step++) {
    if (step === 0) continue
    const y = step * gridSpacingM
    const left = w2c(bounds.minX, y)
    const right = w2c(bounds.maxX, y)
    ctx.beginPath()
    ctx.moveTo(left.cx, left.cy)
    ctx.lineTo(right.cx, right.cy)
    ctx.stroke()

    if (formatDistance) {
      ctx.save()
      ctx.globalAlpha = 0.75
      ctx.fillStyle = line
      ctx.font =
        '11px -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif'
      ctx.textAlign = 'left'
      ctx.textBaseline = 'middle'
      ctx.fillText(formatDistance(y), left.cx + 6, left.cy - 1)
      ctx.restore()
    }
  }
  ctx.globalAlpha = 1

  // Hash marks — two vertical rows of per-yard ticks inset from each
  // sideline, matching the marks on a real football pitch. Skip the
  // major gridline yards since those already draw the full line across.
  if (hashInsetM && hashInsetM > 0) {
    const leftHashX = bounds.minX + hashInsetM
    const rightHashX = bounds.maxX - hashInsetM
    const yardStart = Math.ceil(bounds.minY / YARD_M)
    const yardEnd = Math.floor(bounds.maxY / YARD_M)
    ctx.lineWidth = lineWidth
    ctx.strokeStyle = line
    // Ticks run perpendicular to the hash row. The hash row runs along
    // the forward axis (Y in world), so each tick extends in the lateral
    // axis (X). Drawing them in Y would just overlap into a solid line
    // since the tick length (2yd) is larger than the per-yard spacing.
    for (let yd = yardStart; yd <= yardEnd; yd++) {
      const y = yd * YARD_M
      // Skip the row that lines up with a gridline — avoids doubling up.
      if (Math.abs(y % gridSpacingM) < 0.01) continue
      for (const hashX of [leftHashX, rightHashX]) {
        const left = w2c(hashX - HASH_TICK_LEN_M / 2, y)
        const right = w2c(hashX + HASH_TICK_LEN_M / 2, y)
        ctx.beginPath()
        ctx.moveTo(left.cx, left.cy)
        ctx.lineTo(right.cx, right.cy)
        ctx.stroke()
      }
    }
  }

  // Zero / start line in accent purple — the visual anchor every throw
  // radiates from.
  const zeroLeft = w2c(bounds.minX, 0)
  const zeroRight = w2c(bounds.maxX, 0)
  ctx.strokeStyle = startLineColor
  ctx.lineWidth = lineWidth * 1.6
  ctx.beginPath()
  ctx.moveTo(zeroLeft.cx, zeroLeft.cy)
  ctx.lineTo(zeroRight.cx, zeroRight.cy)
  ctx.stroke()

  // Arrows — dim the non-selected throws when one is active so the
  // selection reads clearly against a busy spread.
  const hasSelection = selectedId != null && selectedId !== ''
  for (const arrow of arrows) {
    const isSelected = selectedId === arrow.id
    const opacity = hasSelection && !isSelected ? 0.25 : 1
    const start = w2c(arrow.x, arrow.y)
    const end = w2c(arrow.endX, arrow.endY)
    drawArrow(ctx, start, end, {
      color: arrow.color,
      lineWidth: isSelected ? 3.5 : 2.5,
      headLength: 10,
      opacity
    })
    // Start dot so every throw anchors visibly on the zero line.
    drawPoint(ctx, start, {
      color: arrow.color,
      radius: isSelected ? 5 : 4,
      opacity,
      selected: isSelected
    })
  }
}
